import {
  type Patient,
  patientsClient,
  type PatientSignalment,
} from "@patients/patientsClient";
import { useQueries, useQuery } from "@tanstack/react-query";

export interface Signalment {
  species: string | null;
  breed: string | null;
  sex: string | null;
  age: string | null;
  microchip: string | null;
}

const capitalized = (value: string | null): string | null =>
  value ? value[0].toUpperCase() + value.slice(1) : null;

const NEUTERED: Record<string, string> = { female: "spayed", male: "neutered" };

/** "Female spayed", "Male intact"; brian's `Sex` and `ReproductiveStatus` words. */
export const sexText = (
  sex: string | null,
  reproductiveStatus: string | null,
): string | null => {
  if (!sex) return capitalized(reproductiveStatus);
  const status =
    reproductiveStatus === "neutered"
      ? (NEUTERED[sex] ?? reproductiveStatus)
      : reproductiveStatus;
  return [capitalized(sex), status].filter(Boolean).join(" ");
};

/** Mirrors brian `modules/patient/columns.py` `age_on`. */
const ageFrom = (s: PatientSignalment, today: Date): string | null => {
  if (!s.birthDate || !s.birthDatePrecision) return null;
  const [year, recordedMonth, recordedDay] = s.birthDate.split("-").map(Number);
  const [month, day] =
    s.birthDatePrecision === "year"
      ? [7, 2]
      : s.birthDatePrecision === "month"
        ? [recordedMonth, 15]
        : [recordedMonth, recordedDay];
  let months = (today.getFullYear() - year) * 12 + today.getMonth() + 1 - month;
  if (today.getDate() < day) months -= 1;
  months = Math.max(months, 0);
  const approximate = s.birthDatePrecision === "year" ? "~" : "";
  return months >= 12
    ? `${approximate}${Math.floor(months / 12)} y`
    : `${approximate}${months} mo`;
};

export const signalmentOf = (
  s: PatientSignalment,
  today = new Date(),
): Signalment => ({
  species: capitalized(s.species),
  breed: s.breed,
  sex: sexText(s.sex, s.reproductiveStatus),
  age: ageFrom(s, today),
  microchip: s.microchip,
});

const patientQuery = (patientId: string | null) => ({
  queryKey: ["patients", patientId],
  queryFn: () => patientsClient.get(patientId!),
  enabled: patientId !== null,
});

export const usePatientSignalment = (patientId: string | null) =>
  useQuery({
    ...patientQuery(patientId),
    select: (patient) => signalmentOf(patient.signalment),
  });

export const usePatientSprite = (patientId: string | null) =>
  useQuery({ ...patientQuery(patientId), select: (patient) => patient.sprite });

/** In `patientIds` order, undefined while a patient loads. */
export const usePatientSprites = (patientIds: readonly string[]) =>
  useQueries({
    queries: patientIds.map((patientId) => ({
      ...patientQuery(patientId),
      select: (patient: Patient) => patient.sprite,
    })),
    combine: (results) => results.map((result) => result.data),
  });
